import h from 'hyperscript';
import isEqual from 'lodash-es/isEqual';

import type { Alert } from 'ui/alert/alert';
import type { MenuItem } from 'ui/menu/menu';
import type { AlertDialogType } from 'ui/dialog/alert/alert-dialog';

import { errorAlert } from 'cadenza/alert';
import { logger } from 'cadenza/utils/logging';
import type { TableDataDto } from 'cadenza/ui/base-table/model/table-data-dto';
import type { ConditionWrapper } from 'cadenza/conditions/condition-wrapper';
import { CONDITION_CHANGE } from 'cadenza/conditions/event-types';
import type { FilterController } from 'cadenza/conditions/filter-controller';

import './condition-element.css';
import i18n from './messages.properties';

export interface ConditionValidity {
  valid: boolean;
  tooLong?: boolean;
  valueMissing?: boolean;
}

export interface ValidityMessage {
  title: string;
  message: string;
  type: AlertDialogType;
}

export abstract class ConditionElement extends HTMLElement {

  _changed?: boolean;
  _value?: unknown;
  _defaultValue?: unknown;

  _delayNotification?: Alert;
  _validationNotification?: Alert;

  connectedCallback () {
    this.classList.add('condition');
  }

  /**
   * @return {boolean} Whether the condition changed or not
   */
  get changed () {
    return this._changed!;
  }

  /**
   * @protected
   * @param changed - Whether the condition changed or not
   */
  set changed (changed: boolean) {
    this._changed = changed;
  }

  /**
   * Initializes the changed property based on the value and the default value
   *
   * @protected
   */
  initChanged () {
    this.changed = !isEqual(this.value, this.defaultValue);
  }

  /**
   * @protected
   * @param value - the value of the condition
   */
  set value (value) {
    this._value = value;
  }

  /**
   * @protected
   * @return The value of this condition
   */
  get value () {
    return this._value;
  }

  /**
   * @protected
   * @return The default value for this condition
   */
  get defaultValue () {
    return this._defaultValue;
  }

  /**
   * @protected
   * @param defaultValue - The default value for this condition
   */
  set defaultValue (defaultValue: unknown) {
    this._defaultValue = defaultValue;
  }

  reset (triggerConditionChange = true) {
    this.value = this.defaultValue;
    this.changed = false;
    if (triggerConditionChange) {
      this.onConditionChange();
    }
  }

  get filterController () {
    return this.closest<FilterController>('.d-filter-controller')!;
  }

  get isWorkbookCondition () {
    return !this.filterController;
  }

  /**
   * @protected
   * @return The current conditionValuesSetHash
   */
  get conditionValuesSetHash () {
    // Get the hash from the filter controller,
    // because for selector layers the hash is not maintained in the URL.
    return this.filterController?.conditionValuesSetHash;
  }

  /**
   * @protected
   * @return The condition-set-id
   */
  get conditionSetId () {
    return this.getAttribute('condition-set-id')!;
  }

  get conditionName () {
    return this.getAttribute('condition-name')!;
  }

  get conditionId () {
    return this.getAttribute('persistence-id')!;
  }

  /**
   * @protected
   * @return The condition label
   */
  get conditionLabel () {
    return this.getAttribute('condition-label')!;
  }

  /**
   * @param labelText - text to be set as condition label
   */
  set conditionLabel (labelText: string) {
    this.setAttribute('condition-label', labelText || '');
  }

  get custom () {
    return this.getAttribute('custom') === 'true';
  }

  /**
   * @protected
   * @return Whether the condition is enabled or not
   */
  get enabled () {
    return (this.getAttribute('enabled') === 'true');
  }

  set enabled (enabled: boolean) {
    this.conditionWrapper.setAttribute('enabled', String(enabled));
    this.conditionWrapper.querySelector<HTMLButtonElement>('.d-condition--clear')!.disabled = !enabled;
    this.setAttribute('enabled', String(enabled));
  }

  /**
   * @protected
   * @return The condition wrapper of this condition
   */
  get conditionWrapper () {
    // ! – because it's guaranteed to be there
    return this.closest<ConditionWrapper>('.condition-wrapper')!;
  }

  /**
   * @protected
   * @return The max selection for this condition
   */
  get maxSelection () {
    return Number(this.getAttribute('max-selection'));
  }

  /**
   * @protected
   * @return Whether this is a custom condition or not.
   *   If the remove-only attribute is set, then this is a custom condition
   */
  get removeOnly () {
    return this.getAttribute('remove-only') === 'true';
  }

  /**
   * @protected
   * @return Whether it's required to set value on this condition
   */
  get _required () {
    return this.hasAttribute('required');
  }

  /**
   * @protected
   * @return The number of condition value rows
   */
  get numberOfConditionValueRows () {
    return 0;
  }

  get hasValue () {
    return this.numberOfConditionValueRows > 0;
  }

  get enables () {
    // ! – because it's guaranteed to be there
    return JSON.parse(this.getAttribute('enables')!);
  }

  get validity (): ConditionValidity {
    const valueMissing = (this.enabled && this._required && !this.hasValue);
    const tooLong = (
      this.enabled
      && this.maxSelection > 0
      && this.numberOfConditionValueRows > this.maxSelection
    );
    return {
      tooLong,
      valueMissing,
      valid: !(valueMissing || tooLong)
    };
  }

  setDelayNotification (isDelayed: boolean) {
    if (this._delayNotification) {
      this.removeChild(this._delayNotification);
      this._delayNotification = undefined;
    }
    if (isDelayed) {
      const message = i18n('condition.delayed.message');
      const className = '.d-alert-info';
      this._delayNotification = h<Alert>('d-alert.d-alert-small' + className, { message });
      this.appendChild(this._delayNotification);
    }
  }

  reportValidity (validity = this.validity) {
    if (!validity.valid) {
      if (this._validationNotification) {
        this.removeChild(this._validationNotification);
      }
      // ! – because subclasses are supposed to override this method to handle any additional validation errors
      const validationMessage = this.getValidationTitleAndMessage(validity)!;
      if (validationMessage?.message) {
        this._validationNotification = h('d-alert.d-alert-small', {
          message: validationMessage.message,
          type: validationMessage?.type || 'warning' });
        this.appendChild(this._validationNotification);
      } else {
        this._validationNotification = undefined;
      }
    } else if (this._validationNotification) {
      this.removeChild(this._validationNotification);
      this._validationNotification = undefined;
    }
    return validity.valid;
  }

  /**
   * Gets the correct validation title and message for the condition during the validation phase
   *
   * @protected
   * @param validity - the validity object to help discriminate what validation error are we having
   * @return an object representing the corresponding validation error message
   */
  getValidationTitleAndMessage (validity: ConditionValidity): ValidityMessage | undefined {
    if (validity.valueMissing) {
      return {
        title: '',
        message: i18n('condition.validity.valueMissing.message'),
        type: 'warning'
      };
    } else if (validity.tooLong) {
      return {
        title: i18n('condition.validity.tooLong.title'),
        message: i18n('condition.validity.tooLong.message', { max: this.maxSelection }),
        type: 'warning'
      };
    }
  }

  /**
   * Dispatch the condition change event
   *
   * @protected
   * @param conditionValuesSetHashObject - detail data for the event
   */
  onConditionChange (conditionValuesSetHashObject?: Record<string, string>) {
    const oldConditionValuesSetHash = this.conditionValuesSetHash;
    this.initChanged();
    const detail = Object.assign(
      {},
      conditionValuesSetHashObject,
      {
        conditionId: this.conditionId,
        oldConditionValuesSetHash
      }
    );
    this.dispatchEvent(new CustomEvent(CONDITION_CHANGE, {
      bubbles: true,
      detail
    }));
  }

  /**
   * Show the user the generic errorAlert and log the error
   *
   * @protected
   * @param error - the error, that occurred
   */
  handleConditionValueChangeError (error: Error) {
    logger.error('Cannot set conditionValue', error);
    errorAlert(
      i18n('error.dialog.title'),
      i18n('error.dialog.message'));
  }

  /**
   * Returns true if a map is currently displayed.
   *
   * @return if a map is currently displayed
   */
  isMapDisplayed () {
    return !!document.getElementById('map');
  }

  set labelledBy (elementOrId: HTMLElement | string) {
    // implement in sub-classes
  }

  /**
   * The ID of the element which labels this condition element.
   *
   * @protected
   */
  get labelledBy (): string | null {
    return null; // implement in sub-classes
  }

  set describedBy (elementOrId: HTMLElement | string) {
    // implement in sub-classes
  }

  /**
   * The ID of the element which describes this condition element.
   *
   * @protected
   */
  get describedBy (): string | null {
    return null; // implement in sub-classes
  }

  /**
   * Condition elements are not focusable themselves. Sub-classes should override this to focus the right sub-control.
   */
  override focus () {
    super.focus(); // implement in sub-classes
  }

  /**
   * This method should be overridden if the concrete sub-class needs additional action items
   * in the condition's context menu in the workbook.
   *
   * This method should be used for actions that:
   * - Are specific to this type of condition
   * - Do not depend on context
   * - Act on and depend on the internal condition component's state
   *
   * The context-sensitive actions are injected from outside of the component, in the wrapper.
   *
   * @see WorkbookConditionWrapper._createMenuActions
   * @see ConditionsController.createMenuItemsForCondition
   * @see QLCondition.getSpecificActionsForWorkbooks, TableCondition.getSpecificActionsForWorkbooks
   * @return A list of actions specific to that condition type (if any).
   */
  getSpecificActionsForWorkbooks (): MenuItem[] {
    return [];
  }

  get contextSensitive () {
    return false;
  }

  /**
   * Sets context-sensitive values fetched when other conditions where changed.
   * Is overridden by sublasses which support context sensitivity.
   *
   * @protected
   * @param contextSensitiveValues - context-sensitive restriction details
   */
  set contextSensitiveValues (contextSensitiveValues: TableDataDto) {
    // overwritten in subclasses, as necessary
  }

  getHeaderButtons (): HTMLButtonElement[] {
    return [];
  }

  /**
   * Whether this condition element provides help content.
   *
   * @return True if this condition element provides help content.
   */
  get hasHelpContent () {
    return false;
  }

  /**
   * A function that provides help content to be presented in a popup.
   *
   * @return Returns the content or null if not applicable.
   */
  helpContentProvider (): HTMLElement | undefined {
    return undefined;
  }

}
